import { useEffect, useReducer, useState } from "react";
import {
    Stack,
    Card,
    CardContent,
    Typography,
    Chip,
    Avatar,
    Box,
    CircularProgress,
    Tooltip
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import BusinessIcon from "@mui/icons-material/Business";
import GoogleIcon from "@mui/icons-material/Google";
import { useSearchParams } from "react-router-dom";

import {
    fetchUserIntegrations,
    startEmailIntegrationLogin,
    removeEmailIntegration,
} from "../../services/user.service";
import { useDispatch } from "react-redux"; 

/* ---------- reducer ---------- */
const reducer = (state, action) => {
    switch (action.type) {
        case "SET":
            return { ...state, [action.provider]: { ...action.payload, loading: false } };
        case "LOADING":
            return {
                ...state,
                [action.provider]: { ...state[action.provider], loading: action.flag },
            };
        default:
            return state;
    }
};

/* ---------- tile component ---------- */
function ProviderTile({ provider, icon, name, state, onToggle, disabled = false }) {
    const { connected, email, loading } = state;
    const ACTION_W = 180; // fixed width so nothing shifts

    return (
        <Card sx={{ p: 2, borderRadius: 2, boxShadow: 3 }}>
            <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {/* left: icon + text */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ bgcolor: "primary.light" }}>{icon}</Avatar>
                        <Stack>
                            <Typography fontWeight={500}>{name}</Typography>
                            {connected && !loading && (
                                <Typography variant="caption" color="text.secondary">
                                    {email}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>

                    {/* right: status + action */}
                    <Box sx={{ width: ACTION_W, display: "flex", justifyContent: "flex-end" }}>
                        {connected ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                                <Chip label="Connected" color="success" size="small" sx={{ flexShrink: 0 }} />
                                <LoadingButton
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    loading={loading}
                                    onClick={() => onToggle(provider, false)}
                                    sx={{ width: "100%" }}
                                    startIcon={<LinkOffIcon />}
                                >
                                    Disconnect
                                </LoadingButton>
                            </Stack>
                        ) : (
                            <Tooltip title={disabled ? "Coming soon" : ""}>
                                <span style={{ width: "100%" }}>
                                    <LoadingButton
                                        variant="contained"
                                        loading={loading}
                                        onClick={() => onToggle(provider, true)}
                                        sx={{ width: "100%" }}
                                        disabled={disabled}
                                    >
                                        Connect
                                    </LoadingButton>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

/* ---------- main page ---------- */
export default function EmailIntegrations() {
    const [integrations, dispatch] = useReducer(reducer, {
        microsoft: { connected: false, email: null, loading: true }, // spinner first
        google: { connected: false, email: null, loading: true },
    });

    const [pageLoading, setPageLoading] = useState(true);          // 👈 page spinner

    /* fetch helper */
    const loadIntegrations = async () => {
        const data = await fetchUserIntegrations();
        dispatch({ type: "SET", provider: "microsoft", payload: data.microsoft });
        dispatch({ type: "SET", provider: "google", payload: data.google });
        setPageLoading(false);
    };

    const reduxDispatch = useDispatch(); 

    useEffect(() => {
        loadIntegrations();
    }, []);

    const [searchParams] = useSearchParams();
    const redirectAfter = searchParams.get("redirectAfter"); 

    /* toggle handler */
    const handleToggle = async (provider, connect) => {
        dispatch({ type: "LOADING", provider, flag: true });

        if (connect) {
            await startEmailIntegrationLogin(provider, redirectAfter); // redirect
        } else {
            await removeEmailIntegration(provider);
            await loadIntegrations();
            reduxDispatch({
                type: "UPDATE_PROVIDER_CONNECTION",
                payload: {
                    provider: provider,
                },
            });
        }
    };

    /* ---------- render ---------- */
    if (pageLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Stack spacing={3} mx="auto">
            <ProviderTile
                provider="microsoft"
                icon={<BusinessIcon />}
                name="Microsoft Outlook / Office 365"
                state={integrations.microsoft}
                onToggle={handleToggle}
            />

            <ProviderTile
                provider="google"
                icon={<GoogleIcon />}
                name="Google Gmail"
                state={integrations.google}
                onToggle={handleToggle}
            />
        </Stack>
    );
}
